/**
 * src/lib/xuatExcel.js — biến mô tả bảng tính thành file .xlsx tải về.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * VÌ SAO TỰ VIẾT THAY VÌ DÙNG THƯ VIỆN
 *
 * Hai thư viện phổ biến đều có vấn đề với dự án này: một thư viện kéo theo lỗ
 * hổng bảo mật, cái còn lại nặng khoảng 600 KB. File xlsx thực chất là một tệp
 * zip chứa vài file XML — phần cần cho bảng có công thức và định dạng số vừa
 * đủ ngắn để tự viết, và chỉ cần thêm một thư viện nén 8 KB.
 *
 * Đổi lại: không hỗ trợ biểu đồ, ảnh, gộp ô, nhiều kiểu viền. Bảng gửi nhà đầu
 * tư không cần những thứ đó.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Thư viện nén được nạp lười — chỉ tải khi người dùng thực sự bấm nút xuất.
 */

import { DINH_DANG, KIEU } from '@shared/excel/sheet.js';

function thoat(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function cotChu(i) {
  let s = '';
  let n = i;
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

/**
 * BẢNG STYLE
 *
 * Một ô có hai thuộc tính hình thức: KIỂU (font, nền, viền, căn lề) và ĐỊNH
 * DẠNG SỐ. Số style trong file là tích của hai tập đó, sinh sẵn một lần ở
 * đây; ô chỉ cần tra chỉ số. Cách này để thêm một kiểu ô mới không phải đụng
 * tới phần ghi XML.
 */
const MA_DINH_DANG = Object.keys(DINH_DANG);
const MA_KIEU = Object.keys(KIEU);

/** Danh sách font, nền, viền dùng thật — gom lại để không lặp trong styles.xml. */
const khoaFont = (k) => JSON.stringify(KIEU[k].font);
const FONTS = [...new Set(MA_KIEU.map(khoaFont))];
const NENS = [null, null, ...new Set(MA_KIEU.map((k) => KIEU[k].nen).filter(Boolean))]; // 2 fill đầu là bắt buộc theo chuẩn
const VIENS = [null, 'hop', 'duoi', 'tren'];

const chiSoFont = (k) => FONTS.indexOf(khoaFont(k));
const chiSoNen = (k) => (KIEU[k].nen ? NENS.indexOf(KIEU[k].nen) : 0);
const chiSoVien = (k) => (KIEU[k].vien ? VIENS.indexOf(KIEU[k].vien) : 0);

/** Chỉ số style của một ô: (kiểu, định dạng số). */
function chiSoStyle(oData) {
  const kieu = KIEU[oData.kieu] ? oData.kieu : 'mac-dinh';
  // Ô chữ không áp định dạng số — dùng 'CHU' để giữ nguyên chuỗi.
  const dinhDang = oData.loai === 'chu' ? 'CHU' : (oData.dinhDang || 'SO');
  const i = MA_DINH_DANG.indexOf(dinhDang);
  return MA_KIEU.indexOf(kieu) * MA_DINH_DANG.length + (i < 0 ? 0 : i);
}

function oXml(cot, hangSo, oData) {
  if (!oData || oData.loai === 'trong') return '';
  const dc = `${cotChu(cot)}${hangSo}`;
  const s = chiSoStyle(oData);

  if (oData.loai === 'congThuc') {
    // Không ghi giá trị tính sẵn. Excel và Google Sheets đều tự tính khi mở.
    return `<c r="${dc}" s="${s}"><f>${thoat(oData.f)}</f></c>`;
  }
  if (oData.loai === 'soTrong') return `<c r="${dc}" s="${s}"/>`;
  if (oData.loai === 'so') {
    return `<c r="${dc}" s="${s}"><v>${oData.v}</v></c>`;
  }
  // Chuỗi ghi thẳng vào ô, không dùng bảng chuỗi dùng chung — đơn giản hơn và
  // với vài trăm ô thì chênh lệch dung lượng không đáng kể.
  return `<c r="${dc}" s="${s}" t="inlineStr"><is><t xml:space="preserve">${thoat(oData.v)}</t></is></c>`;
}

function toaDo(dc) {
  const m = /^([A-Z]+)(\d+)$/.exec(String(dc || '').toUpperCase());
  if (!m) return null;
  let cot = 0;
  for (const ch of m[1]) cot = cot * 26 + (ch.charCodeAt(0) - 64);
  return { cot: cot - 1, hang: Number(m[2]) - 1, dc: m[0] };
}

function bangXml(b) {
  const cols = b.rongCot?.length
    ? `<cols>${b.rongCot.map((w, i) =>
        `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>`
    : '';

  // Đóng băng tiêu đề: bảng 12 tháng cuộn ngang mà mất tên dòng thì không đọc được.
  const bang = toaDo(b.dongBang);
  const view = `<sheetView workbookViewId="0"${b.anLuoi === false ? '' : ' showGridLines="0"'}>${
    bang ? `<pane xSplit="${bang.cot}" ySplit="${bang.hang}" topLeftCell="${bang.dc}" activePane="bottomRight" state="frozen"/>`
      + `<selection pane="bottomRight" activeCell="${bang.dc}" sqref="${bang.dc}"/>` : ''
  }</sheetView>`;

  const rows = b.hang.map((h, i) => {
    const oS = h.map((c, j) => oXml(j, i + 1, c)).join('');
    // Dải tên tab cao hơn một chút cho thoáng.
    const cao = h[0]?.kieu === 'tieu-de-tab' ? ' ht="24" customHeight="1"' : '';
    return `<row r="${i + 1}"${cao}>${oS}</row>`;
  }).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetPr><outlinePr/></sheetPr><sheetViews>${view}</sheetViews>
<sheetFormatPr defaultRowHeight="15"/>${cols}<sheetData>${rows}</sheetData></worksheet>`;
}

function fontXml(khoa) {
  const f = JSON.parse(khoa);
  return `<font><sz val="${f.co}"/><color rgb="${f.mau}"/><name val="${f.ten}"/>${f.dam ? '<b/>' : ''}${f.nghieng ? '<i/>' : ''}</font>`;
}

function vienXml(kieu) {
  if (!kieu) return '<border><left/><right/><top/><bottom/><diagonal/></border>';
  const mong = `<color rgb="FFBFCBD6"/>`;
  if (kieu === 'hop') return `<border><left style="thin">${mong}</left><right style="thin">${mong}</right><top style="thin">${mong}</top><bottom style="thin">${mong}</bottom><diagonal/></border>`;
  if (kieu === 'duoi') return `<border><left/><right/><top/><bottom style="medium"><color rgb="FF0F3057"/></bottom><diagonal/></border>`;
  return `<border><left/><right/><top style="thin"><color rgb="FF0F3057"/></top><bottom/><diagonal/></border>`;
}

function stylesXml() {
  const numFmts = MA_DINH_DANG.map((k, i) =>
    `<numFmt numFmtId="${164 + i}" formatCode="${thoat(DINH_DANG[k])}"/>`).join('');

  const fonts = FONTS.map(fontXml).join('');
  const fills = `<fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>`
    + NENS.slice(2).map((mau) => `<fill><patternFill patternType="solid"><fgColor rgb="${mau}"/><bgColor indexed="64"/></patternFill></fill>`).join('');
  const borders = VIENS.map(vienXml).join('');

  // Tích (kiểu × định dạng số) — thứ tự phải khớp với chiSoStyle().
  const xfs = MA_KIEU.flatMap((k) => MA_DINH_DANG.map((dd, i) => {
    const kieu = KIEU[k];
    const canh = kieu.canh || (dd === 'CHU' ? 'left' : 'right');
    const thut = kieu.thut ? ` indent="${kieu.thut}"` : '';
    const soId = dd === 'CHU' ? 0 : 164 + i;
    return `<xf numFmtId="${soId}" fontId="${chiSoFont(k)}" fillId="${chiSoNen(k)}" borderId="${chiSoVien(k)}" xfId="0"`
      + ` applyFont="1" applyFill="1" applyBorder="1" applyNumberFormat="1" applyAlignment="1">`
      + `<alignment horizontal="${canh}" vertical="center"${thut}/></xf>`;
  })).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="${MA_DINH_DANG.length}">${numFmts}</numFmts>
<fonts count="${FONTS.length}">${fonts}</fonts>
<fills count="${NENS.length}">${fills}</fills>
<borders count="${VIENS.length}">${borders}</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="${MA_KIEU.length * MA_DINH_DANG.length}">${xfs}</cellXfs>
</styleSheet>`;
}

/** Trả về Uint8Array nội dung file .xlsx. */
export async function taoXlsx(tepMoTa) {
  const { zipSync, strToU8 } = await import('fflate');
  const bangs = tepMoTa.cacBang;

  const files = {
    '[Content_Types].xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
${bangs.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`),

    '_rels/.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`),

    'xl/workbook.xml': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${bangs.map((b, i) =>
  `<sheet name="${thoat(b.ten)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>
<calcPr fullCalcOnLoad="1"/>
</workbook>`),

    'xl/_rels/workbook.xml.rels': strToU8(`<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${bangs.map((_, i) =>
  `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')}
<Relationship Id="rId${bangs.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`),

    'xl/styles.xml': strToU8(stylesXml()),
  };

  bangs.forEach((b, i) => {
    files[`xl/worksheets/sheet${i + 1}.xml`] = strToU8(bangXml(b));
  });

  return zipSync(files, { level: 6 });
}

/** Tạo file và kích hoạt tải về trên trình duyệt. */
export async function taiVe(tepMoTa, tenFile) {
  const bytes = await taoXlsx(tepMoTa);
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = tenFile.endsWith('.xlsx') ? tenFile : `${tenFile}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
